import { ApplicationConfig, Type, enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { appSettings } from '@nha/config';
import { UserSessionService } from '../services/user-session.service';
import { ReAuthenticationRequiredError } from '../types/re-authentication-required.error';
import { UserProfileLoadError } from '../types/user-profile-load-error.type';
import { renderBootstrapError } from './bootstrap-error.functions';

/**
 * One re-login attempt, so a token the server keeps rejecting can't bounce the user between Okta
 * and the app forever. Lives in localStorage because logout() (and an expired-token refresh)
 * clears sessionStorage, which would erase the flag before the redirect and defeat the guard.
 */
const AUTO_LOGOUT_FLAG = 'nha.bootstrap.auto-logout';

export interface NhaBootstrapOptions extends ApplicationConfig {
	/** Also bootstrap inside an iframe when the page was embedded by the legacy shell (?fromLegacy=true). */
	allowLegacyIframe?: boolean;
}

/**
 * The shared myNHA bootstrap sequence every app's main.ts used to copy by hand: prod mode,
 * sign-in/redirect, the iframe guard, session init, and — new — a full-screen error page when the
 * profile can't be loaded, so a failure looks the same in every app instead of a stuck loader.
 */
export function bootstrapNhaApp(rootComponent: Type<unknown>, options: NhaBootstrapOptions): Promise<void> {
	if (appSettings.production) {
		enableProdMode();
	}

	const userSessionService: UserSessionService = new UserSessionService();

	if (!userSessionService.isAuthenticated()) {
		userSessionService.login();
		return Promise.resolve();
	}

	if (userSessionService.isIframe() && !(options.allowLegacyIframe && userSessionService.isLegacy())) {
		// A hidden token-refresh iframe: the page only exists to store the new cookie.
		return Promise.resolve();
	}

	const appConfig: ApplicationConfig = { providers: options.providers };

	return userSessionService
		.init()
		.then(() => bootstrapApplication(rootComponent, appConfig))
		.then(() => {
			localStorage.removeItem(AUTO_LOGOUT_FLAG);
		})
		.catch((error) => {
			console.error(error);

			// The Okta session is gone (e.g. overnight tab): straight to the interactive sign-in,
			// which returns to this URL — an error page would just flash during that round-trip.
			if (error instanceof ReAuthenticationRequiredError) {
				userSessionService.reAuthenticate();
				return;
			}

			// A 401/403 means the server rejected the token itself: retry once with a freshly issued
			// token (the Okta session is kept, so a live session re-issues without a credential prompt).
			if (error instanceof UserProfileLoadError && [401, 403].includes(error.status) && !localStorage.getItem(AUTO_LOGOUT_FLAG)) {
				localStorage.setItem(AUTO_LOGOUT_FLAG, 'true');
				userSessionService.reAuthenticate();
				return;
			}

			renderBootstrapError(error, { onSignOut: () => userSessionService.logout() });
		});
}
